import { Wishlist } from '@theme/wishlist-storage';

/**
 * A custom element used to toggle a product in the wishlist.
 *
 * Usage:
 *   <wishlist-button
 *     data-product-id="123"
 *     data-product-handle="candy-oorringen"
 *     data-variant-id="456"
 *   >…</wishlist-button>
 *
 * The button reflects its state via the `aria-pressed` attribute and the
 * `wishlist-button--active` modifier class, so the visual treatment can be
 * driven entirely from CSS (see `wishlist-button.liquid`).
 */
class WishlistButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;

    this.#syncState();
    this.#button.addEventListener('click', this.#handleClick);
    window.addEventListener(Wishlist.CHANGE_EVENT, this.#handleWishlistChange);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    window.removeEventListener(Wishlist.CHANGE_EVENT, this.#handleWishlistChange);
  }

  /**
   * Returns the product id this button targets.
   * @returns {string | null}
   */
  get productId() {
    return this.dataset.productId ?? null;
  }

  /**
   * Returns the product handle this button targets.
   * @returns {string | null}
   */
  get productHandle() {
    return this.dataset.productHandle ?? null;
  }

  /**
   * Returns the optional variant id this button targets.
   * @returns {string | null}
   */
  get variantId() {
    return this.dataset.variantId ?? null;
  }

  /**
   * Handles user clicks on the underlying button.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.productId || !this.productHandle) return;

    const result = Wishlist.toggle({
      id: this.productId,
      handle: this.productHandle,
      variantId: this.variantId ?? undefined,
    });

    if (result === 'noop') return;

    // Provide a small visual cue (CSS handles the animation).
    this.#button?.classList.add('wishlist-button--pulse');
    setTimeout(() => this.#button?.classList.remove('wishlist-button--pulse'), 400);
  };

  /**
   * Listens for wishlist changes and re-syncs the visual state.
   * @param {CustomEvent} event
   */
  #handleWishlistChange = (event) => {
    const changedId = event.detail?.id;
    // Sync on global events (clear/init) and on changes targeting this product.
    if (!changedId || changedId === this.productId) {
      this.#syncState();
    }
  };

  /**
   * Reads the wishlist state and reflects it on the underlying button.
   */
  #syncState() {
    if (!this.#button || !this.productId) return;
    const isActive = Wishlist.has(this.productId);

    this.#button.setAttribute('aria-pressed', String(isActive));
    this.#button.classList.toggle('wishlist-button--active', isActive);

    const label = isActive
      ? this.dataset.labelRemove ?? this.#button.dataset.labelRemove
      : this.dataset.labelAdd ?? this.#button.dataset.labelAdd;
    if (label) {
      this.#button.setAttribute('aria-label', label);
      this.#button.setAttribute('title', label);
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
